/**
 * Pure gating logic for the per-field KYC review controls.
 *
 * Compliance intent: a staff reviewer must actually OPEN a submitted
 * document before they can approve or reject the field that carries it.
 * The act of viewing is the evidence-of-review that backs the decision
 * (documented, attributable KYC review). Scalar
 * fields (text/number/date) have nothing to open, so they are never
 * view-gated.
 *
 * Kept as a side-effect-free helper so it can be unit-tested without a
 * DOM (the repo has no @testing-library; component logic lives here).
 */

export interface DocumentViewGateInput {
  /** True when the field's value is a file pointer the agent can open. */
  readonly isFile: boolean;
  /** The file's case-document UUID, present when `isFile`. */
  readonly documentId?: string;
  /** Document ids the agent has opened in the viewer this session. */
  readonly viewedDocIds: ReadonlySet<string>;
}

/**
 * Whether a field still requires the agent to view its document before
 * Approve/Reject may be enabled. Always `false` for non-file fields.
 */
export function needsDocumentView({
  isFile,
  documentId,
  viewedDocIds,
}: DocumentViewGateInput): boolean {
  if (!isFile || !documentId) return false;
  return !viewedDocIds.has(documentId);
}
